import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Avatar,
    Button,
    Collapse,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tabs,
    Tab,
    Paper,
    Grid,
    Chip
} from '@mui/material';
import { 
    MessageButton, 
    ManageButton, 
    RemoveButton 
} from '../common/StyledComponents';
import {
    ServiceRow,
    ServiceInfo,
    ActionButtons,
    ExpandedSection,
} from '../common/BookingsManagerStyles';
import VenueDetailView from '../venue/VenueDetailView';
import VenueCard from './VenueCard';
import VenueAutofillForm from '../createEvent/forms/VenueAutofillForm';
import ServiceAutofillForm from './ServiceAutofillForm';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import axios from 'axios';
import { getBackendUserUrl } from '../../utils/utils';
import ServiceCard from './ServiceCard';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ServiceSection = ({ 
    sectionTitle, 
    eventDetails,
    services = [], 
    onManageClick, 
    expandedServiceId,
    onMessageClick,
    onRemoveClick,
    renderExpandedContent,
    onVenueAdded,
    onServiceSelected
}) => {
    const [showRecommendations, setShowRecommendations] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [recommendedItems, setRecommendedItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [filteredServices, setFilteredServices] = useState([]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleItemSelected = (item) => {
        console.log("handleItemSelected: item ", item);
        
        // Structure the service data consistently
        const serviceData = {
            ...item,
            // Ensure we have the correct ID field
            id: item.addonId || item.id,
            // Ensure we have the correct name field
            name: item.companyName || item.name,
            // Ensure we have the correct type based on section
            addonType: sectionTitle.toLowerCase() === 'sponsors' ? 'general sponsor' : sectionTitle.toLowerCase(),
            // Preserve any existing fields
            ...(item.packages && { packages: item.packages }),
            ...(item.selectedPackage && { selectedPackage: item.selectedPackage })
        };

        console.log("Structured service data in ServiceSection:", serviceData);

        if (onServiceSelected) {
            onServiceSelected(serviceData);
        }
    };

    // Add useEffect to handle venue updates
    useEffect(() => {
        console.log("ServiceSection useEffect services", services);
        if (services && services.length > 0) {
            const updatedServices = services.map(service => {
                // If this is a venue service, ensure it has the correct structure
                if (service.venueId) {
                    return {
                        ...service,
                        id: service.venueId,
                        name: service.title,
                        logo: service.coverPhoto,
                        description: service.desc || service.description,
                        capacity: service.capacity,
                        features: service.features,
                        company: service.company
                    };
                }
                return service;
            });
            console.log("ServiceSection useEffect updatedServices", updatedServices);
            setFilteredServices(updatedServices);
        } else {
            setFilteredServices([]);
        }
    }, [services]);

    const handleConfirmItem = () => {
        if (selectedItem && onServiceSelected) {
            onServiceSelected(selectedItem);
            setSelectedItem(null);
            setShowRecommendations(false);
        }
    };

    const fetchRecommendations = async () => {
        setIsLoading(true);
        setError(null);
        try {
            let endpoint = '';
            let params = {};

            // Set endpoint and params based on section type
            // TODO make actual recommendations 
            const sectionType = sectionTitle?.toLowerCase() || '';
            switch (sectionType) {
                case 'venue':
                    endpoint = 'venue/temp_get_recommended_venues';
                    break;
                case 'catering':
                    endpoint = 'company/temp_get_recommended_company';
                    params = {
                        addon_type: 'catering'
                    };
                    break;
                case 'sponsors':
                    endpoint = 'company/temp_get_recommended_company';
                    params = {
                        addon_type: 'sponsors'
                    };
                    break;
                case 'other services':
                    endpoint = 'company/temp_get_recommended_company';
                    params = {
                        addon_type: 'other services'
                    };
                    break;
                default:
                    throw new Error('Invalid section type');
            }

            const response = await axios.post(`${getBackendUserUrl()}/${endpoint}`, params);
            console.log("Recommendations response:", response.data);
            setRecommendedItems(response.data);
        } catch (err) {
            console.error("Error fetching recommendations:", err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (showRecommendations) {
            fetchRecommendations();
        }
    }, [showRecommendations, sectionTitle]);

    const renderRecommendationContent = () => {
        const sectionType = sectionTitle?.toLowerCase() || '';
        switch (sectionType) {
            case 'venue':
                return (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        overflowX: 'auto',
                        pb: 2,
                        '&::-webkit-scrollbar': {
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '4px',
                            '&:hover': {
                                background: '#555',
                            },
                        },
                    }}>
                        {recommendedItems.map((item) => (
                            <Box key={item.venueId} sx={{ minWidth: 280, maxWidth: 280 }}>
                                <VenueCard
                                    name={item.title || item.name}
                                    image={item.coverPhoto || item.cover_photo}
                                    stats={[
                                        { 
                                            icon: '👥', 
                                            value: `${item.capacity?.seated || item.seatedCapacity || 0} seated` 
                                        },
                                        { 
                                            icon: '📍', 
                                            value: item.city || item.location || 'Location TBD' 
                                        }
                                    ]}
                                    links={item.website ? [
                                        { 
                                            url: item.website,
                                            icon: '🌐'
                                        }
                                    ] : []}
                                    venueData={item}
                                    onAddToCart={(venueData) => {
                                        if (onVenueAdded) {
                                            onVenueAdded(venueData);
                                        }
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                );
            case 'catering':
            case 'sponsors':
            case 'vendors':
                return (
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        overflowX: 'auto',
                        pb: 2,
                        '&::-webkit-scrollbar': {
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '4px',
                            '&:hover': {
                                background: '#555',
                            },
                        },
                    }}>
                        {recommendedItems.map((item) => (
                            <Box key={item.addonId || item.id} sx={{ minWidth: 280, maxWidth: 280 }}>
                                <ServiceCard
                                    name={item.name || item.companyName}
                                    addon={item}
                                    image={item.logo || item.logoUrl}
                                    description={item.description || item.desc}
                                    price={item.price}
                                    packages={item.packages}
                                    onServiceSelected={handleItemSelected}
                                />
                            </Box>
                        ))}
                    </Box>
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        console.log(`ServiceSection ${sectionTitle} - Current services:`, services);
        console.log(`ServiceSection ${sectionTitle} - Filtered services:`, filteredServices);
    }, [services, filteredServices, sectionTitle]);
    
    console.log("ServiceSection services", services);

    return (
        <Box sx={{ width: '100%', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h2" marginTop={4}>
                    {sectionTitle}
                </Typography>
                <Button 
                    variant="contained" 
                    onClick={() => setShowRecommendations(!showRecommendations)}
                    startIcon={showRecommendations ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ 
                        backgroundColor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                            backgroundColor: 'primary.dark'
                        }
                    }}
                >
                    Add {sectionTitle}
                </Button>
            </Box>

            {showRecommendations && (
                <Box sx={{ mb: 3 }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        sx={{ 
                            borderBottom: 1, 
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 500
                            }
                        }}
                    >
                        <Tab label="Add New" />
                        <Tab label="Recommended" />
                        
                    </Tabs>

                    {activeTab === 1 ? (
                        <Box sx={{ 
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                bottom: 0,
                                width: '50px',
                                background: 'linear-gradient(to right, transparent, white)',
                                pointerEvents: 'none'
                            }
                        }}>
                            {isLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                    <CircularProgress />
                                </Box>
                            ) : error ? (
                                <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
                            ) : (
                                renderRecommendationContent()
                            )}
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {sectionTitle?.toLowerCase() === 'venue' ? (
                                <VenueAutofillForm onVenueSelected={handleItemSelected} />
                            ) : (
                                <ServiceAutofillForm 
                                    serviceType={sectionTitle?.toLowerCase()}
                                    onServiceSelected={handleItemSelected}
                                />
                            )}
                        </Grid>
                    )}
                </Box>
            )}

            {filteredServices.length > 0 ? (
                filteredServices.map((service) => (
                    <React.Fragment key={service.id}>
                        <ServiceRow>
                            <ServiceInfo>
                                <Avatar
                                    src={service.logo}
                                    alt={service.name}
                                    sx={{ width: 32, height: 32 }}
                                />
                                <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        {service.name}
                                    </Typography>
                                    {!service.isOnboarded && (
                                        <Chip 
                                            label="Onboarding Required" 
                                            size="small" 
                                            color="warning"
                                            sx={{ 
                                                mt: 0.5,
                                                height: '20px',
                                                '& .MuiChip-label': {
                                                    fontSize: '0.75rem',
                                                    px: 1
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                            </ServiceInfo>
                            <ActionButtons>
                                <MessageButton 
                                    variant="message"
                                    onClick={() => onMessageClick?.(service)}
                                >
                                    Message
                                </MessageButton>
                                {service.status === 'pending' ? (
                                     <ManageButton 
                                        variant="manage"
                                        onClick={() => onManageClick?.(service)}
                                    >
                                        Manage
                                    </ManageButton>
                                ) : (
                                    <ManageButton 
                                        variant="manage"
                                        onClick={() => onManageClick?.(service)}
                                    >
                                        Manage
                                    </ManageButton>
                                )}
                            </ActionButtons>
                        </ServiceRow>
                        {expandedServiceId === service.companyId && (
                            <ExpandedSection>
                                {renderExpandedContent?.(service)}
                            </ExpandedSection>
                        )}
                    </React.Fragment>
                ))
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No {sectionTitle?.toLowerCase()} services available
                </Typography>
            )}
        </Box>
    );
};

export default ServiceSection; 